import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Calculator, CircleDollarSign, LineChart, Sparkles,
  ShieldCheck, Receipt, Building2, Check, Download, Volume2, VolumeX, Play, Pause,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Self-hosted (backend /api/static/hero.{webm,mp4}) to avoid third-party CORS/referer blocks.
const HERO_VIDEO_WEBM = `${process.env.REACT_APP_BACKEND_URL}/api/static/hero.webm`;
const HERO_VIDEO_MP4 = `${process.env.REACT_APP_BACKEND_URL}/api/static/hero.mp4`;
const DEMO_VIDEO_WEBM = `${process.env.REACT_APP_BACKEND_URL}/api/static/product-demo.webm`;
const DEMO_VIDEO_MP4 = `${process.env.REACT_APP_BACKEND_URL}/api/static/product-demo.mp4`;
const HERO_POSTER = "https://images.unsplash.com/photo-1642790551116-18e150f248e5?auto=format&fit=crop&w=1600&q=60";

// Dark theme tokens
const DARK_BG = "bg-[#0B1220]";
const DARK_CARD = "bg-white/[0.03] border border-white/[0.08]";

function Nav() {
  return (
    <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-[#0B1220]/80 border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="w-8 h-8 rounded-md bg-forest flex items-center justify-center text-white">
            <Building2 className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <span className="font-heading text-xl font-semibold tracking-tight text-white">
            BizCashflow <span className="text-forest-300">Pros</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
          <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">Features</a>
          <a href="#how" className="hover:text-white transition-colors" data-testid="nav-how">How it works</a>
          <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
        </nav>
        <div className="flex items-center gap-4">
          <Link to="/login" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-login">Sign in</Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 rounded-full bg-forest hover:bg-forest-dark text-white text-sm px-4 py-2 font-medium transition-colors"
            data-testid="nav-cta-signup"
          >
            Get started
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = React.useRef(null);

  const tryDemo = async () => {
    setDemoLoading(true);
    const res = await login("demo@cashflowpro.com", "Demo@2026");
    if (res.ok) {
      nav("/app", { replace: true });
    } else {
      setDemoLoading(false);
      toast.error(res.error || "Could not start demo");
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(videoRef.current.muted);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video background */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        poster={HERO_POSTER}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        data-testid="hero-video"
      >
        <source src={HERO_VIDEO_WEBM} type="video/webm" />
        <source src={HERO_VIDEO_MP4} type="video/mp4" />
      </video>
      {/* Overlays */}
      <div className="absolute inset-0 bg-[#0B1220]/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220] via-[#0B1220]/40 to-[#0B1220]" />

      {/* Mute toggle */}
      <button
        type="button"
        onClick={toggleMute}
        className="absolute top-24 right-6 lg:right-8 z-20 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white flex items-center justify-center transition-colors backdrop-blur-sm"
        aria-label={muted ? "Unmute video" : "Mute video"}
        data-testid="hero-mute-toggle"
      >
        {muted ? <VolumeX className="w-4 h-4" strokeWidth={1.75} /> : <Volume2 className="w-4 h-4" strokeWidth={1.75} />}
      </button>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center pt-24 pb-16">
        <div className="text-xs font-semibold tracking-[0.2em] text-forest-300 uppercase mb-6" data-testid="hero-overline">
          Financial Command Center
        </div>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-white">
          Save on taxes.<br />
          Kill your debt.<br />
          <span className="text-forest-300">Extend your runway.</span>
        </h1>
        <p className="mt-8 text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
          BizCashflow Pros is the financial command center for owner-operators and the accountants who serve them.
          Track every deductible dollar, kill debt on a clear timeline, and finally know your runway — without hiring a controller.
        </p>

        <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/[0.04] border border-white/[0.08] px-4 py-1.5 text-xs text-slate-300">
          <span className="inline-flex items-center gap-1 rounded-full bg-forest-300/15 text-forest-300 px-2 py-0.5 font-semibold">SAVE 20%</span>
          <span>·</span>
          <span>Annual billing, cancel anytime</span>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-full bg-forest hover:bg-forest-dark text-white px-7 py-3.5 font-semibold shadow-lg shadow-forest/30 transition-all hover:scale-[1.02]"
            data-testid="hero-cta-primary"
          >
            Start for free <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            type="button"
            onClick={tryDemo}
            disabled={demoLoading}
            className="text-sm text-slate-300 hover:text-white underline underline-offset-4 disabled:opacity-60 transition-colors"
            data-testid="hero-cta-demo"
          >
            {demoLoading ? "Loading demo…" : "Try the demo — no signup"}
          </button>
        </div>

        <div className="mt-6 text-xs text-slate-500 inline-flex flex-wrap items-center justify-center gap-1.5" data-testid="hero-cta-deck-wrap">
          <Download className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span>Product overview:</span>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/bizcashflowpros-overview.pptx`}
            className="hover:text-forest-300 underline underline-offset-4"
            data-testid="hero-cta-deck"
          >
            .pptx
          </a>
          <span className="text-slate-600">·</span>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/bizcashflowpros-overview.pdf`}
            className="hover:text-forest-300 underline underline-offset-4"
            data-testid="hero-cta-deck-pdf"
          >
            .pdf
          </a>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-forest-300" strokeWidth={2.5} /> Bank-statement import</span>
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-forest-300" strokeWidth={2.5} /> AI advisor included</span>
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-forest-300" strokeWidth={2.5} /> No credit card to start</span>
        </div>
      </div>
    </section>
  );
}

function ProductDemo() {
  const videoRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(true);
  const [muted, setMuted] = React.useState(true);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  return (
    <section className="py-24 lg:py-32 bg-[#0B1220] border-t border-white/[0.06]">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-xs font-semibold tracking-[0.2em] text-forest-300 uppercase mb-4">See it in action</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] text-white max-w-3xl mx-auto">
            Watch your money <span className="text-forest-300">tell its story.</span>
          </h2>
          <p className="mt-5 text-slate-400 text-lg max-w-2xl mx-auto">
            From bank statement to boardroom-ready report — the entire flow in under a minute.
          </p>
        </div>

        <div
          className="relative rounded-3xl overflow-hidden border border-white/[0.08] shadow-2xl shadow-black/40 group"
          data-testid="product-demo-container"
        >
          {/* Subtle green glow behind the frame */}
          <div className="absolute -inset-1 bg-forest-300/10 blur-3xl -z-10" />

          <video
            ref={videoRef}
            className="w-full aspect-video object-cover bg-black cursor-pointer"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onClick={togglePlay}
            data-testid="product-demo-video"
          >
            <source src={DEMO_VIDEO_WEBM} type="video/webm" />
            <source src={DEMO_VIDEO_MP4} type="video/mp4" />
          </video>

          {/* Play/Pause + Mute controls (fade in on hover) */}
          <div className="absolute inset-0 flex items-end justify-between p-4 lg:p-6 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={togglePlay}
              className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-black/60 hover:bg-black/80 text-white text-xs font-medium px-4 py-2 backdrop-blur-md border border-white/10 transition-colors"
              data-testid="product-demo-play-toggle"
            >
              {playing ? <Pause className="w-3.5 h-3.5" strokeWidth={2} /> : <Play className="w-3.5 h-3.5" strokeWidth={2} />}
              {playing ? "Pause" : "Play"}
            </button>
            <button
              type="button"
              onClick={toggleMute}
              className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-black/60 hover:bg-black/80 text-white text-xs font-medium px-4 py-2 backdrop-blur-md border border-white/10 transition-colors"
              data-testid="product-demo-mute-toggle"
            >
              {muted ? <VolumeX className="w-3.5 h-3.5" strokeWidth={2} /> : <Volume2 className="w-3.5 h-3.5" strokeWidth={2} />}
              {muted ? "Unmute" : "Mute"}
            </button>
          </div>

          {/* Live badge (top-left) */}
          <div className="absolute top-4 left-4 lg:top-6 lg:left-6 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white text-[11px] font-medium px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-forest-300 animate-pulse" />
            LIVE PRODUCT DEMO
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-6 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-forest-300" strokeWidth={2.5} /> No signup required</span>
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-forest-300" strokeWidth={2.5} /> Real data · real numbers</span>
          <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-forest-300" strokeWidth={2.5} /> ~60 seconds</span>
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  {
    icon: Calculator, title: "Tax savings tracker",
    body: "Every deductible expense — categorized. See your estimated tax bill, quarterly amounts, and missed write-offs in real time.",
  },
  {
    icon: CircleDollarSign, title: "Debt killer",
    body: "Snowball or avalanche — see the exact month every loan disappears and how much interest you save.",
  },
  {
    icon: LineChart, title: "Cash flow runway",
    body: "Real burn, real runway, real top-spend categories. Built for owner-operators, not accountants.",
  },
  {
    icon: Sparkles, title: "AI advisor on call",
    body: "Pick Claude Sonnet 4.5 or GPT-5.2. Ask anything — get an answer grounded in your real numbers.",
  },
  {
    icon: Receipt, title: "Bank statement upload",
    body: "Drop in a PDF or CSV. BizCashflow Pros extracts transactions, suggests categories, and flags deductibles automatically.",
  },
  {
    icon: ShieldCheck, title: "Yours alone",
    body: "Your data lives in your account. No selling, no ads, no surprises. Cancel and export anytime.",
  },
];

function Features() {
  return (
    <section id="features" className="py-24 lg:py-32 bg-[#0B1220]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <div className="text-xs font-semibold tracking-[0.2em] text-forest-300 uppercase mb-4">Features</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] text-white max-w-2xl">
            Everything you need<br />
            to <span className="text-forest-300">stay ahead.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className={`rounded-2xl ${DARK_CARD} p-7 hover:bg-white/[0.05] transition-colors animate-fade-up`}
              style={{ animationDelay: `${i * 0.05}s` }}
              data-testid={`feature-card-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-forest-300/15 text-forest-300 flex items-center justify-center mb-5">
                <f.icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { num: "01", t: "Connect or upload", d: "Drop a bank statement PDF, upload a CSV, or enter transactions manually." },
    { num: "02", t: "We categorize", d: "Every expense gets a category and a deductibility flag — you confirm." },
    { num: "03", t: "Get clarity", d: "Three live dashboards: cash flow, tax savings, debt timeline. Talk to AI when you're stuck." },
  ];
  return (
    <section id="how" className="py-24 lg:py-32 bg-[#0B1220] border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-xs font-semibold tracking-[0.2em] text-forest-300 uppercase mb-4">How it works</div>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-16 max-w-2xl leading-[1.05] text-white">
          From shoebox to runway<br />in under <span className="text-forest-300">five minutes.</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div
              key={s.num}
              className={`${DARK_CARD} rounded-2xl p-8 animate-fade-up`}
              style={{ animationDelay: `${i * 0.1}s` }}
              data-testid={`step-${i}`}
            >
              <div className="num text-sm text-forest-300 font-semibold mb-4">{s.num}</div>
              <h3 className="font-heading text-xl font-semibold mb-3 text-white">{s.t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonial() {
  return (
    <section className="py-24 lg:py-32 bg-[#0B1220] border-t border-white/[0.06]">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-xs font-semibold tracking-[0.2em] text-forest-300 uppercase mb-5 text-center">Customer story</div>
        <blockquote className="font-heading text-3xl sm:text-4xl font-medium leading-snug text-white text-center">
          &ldquo;I found <span className="text-forest-300">$11,400</span> in deductions my old accountant missed.
          Then it told me exactly which credit card to attack first.
          I&apos;m out of debt <span className="text-forest-300">nineteen months earlier.</span>&rdquo;
        </blockquote>
        <div className="mt-8 flex flex-col items-center gap-1 text-center">
          <div className="font-medium text-white">Marcus Chen</div>
          <div className="text-sm text-slate-500">Founder, Chen &amp; Co Bookkeeping</div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const plans = [
    { name: "Solo", price: "Free", desc: "For founders just starting", features: ["Up to 100 transactions/mo", "Tax + debt + cash flow dashboards", "30 AI advisor messages / mo", "CSV import"] },
    { name: "Operator", price: "$24", suffix: "/mo", featured: true, desc: "For growing owner-operators", features: ["Unlimited transactions", "PDF bank statement import", "Unlimited AI advisor (Claude + GPT)", "Quarterly tax projections", "Export to QuickBooks / Xero"] },
    { name: "Studio", price: "$89", suffix: "/mo", desc: "For multi-entity owners", features: ["Everything in Operator", "Up to 5 entities", "Multi-user access", "Dedicated CPA review (quarterly)", "API access"] },
  ];
  return (
    <section id="pricing" className="py-24 lg:py-32 bg-[#0B1220] border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-xs font-semibold tracking-[0.2em] text-forest-300 uppercase mb-4">Pricing</div>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3 text-white">
          Straight prices. <span className="text-forest-300">No upsells.</span>
        </h2>
        <p className="text-slate-400 mb-16 max-w-xl">Start free. Upgrade when your business does.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`p-8 rounded-2xl relative ${
                p.featured
                  ? "bg-forest text-white border border-forest-300/40 shadow-2xl shadow-forest/20"
                  : `${DARK_CARD} text-white`
              }`}
              data-testid={`pricing-${p.name.toLowerCase()}`}
            >
              {p.featured && (
                <div className="absolute -top-3 right-6 rounded-full bg-forest-300 text-forest text-[10px] font-bold uppercase tracking-wider px-3 py-1">
                  Most popular
                </div>
              )}
              <div className={`text-sm font-medium ${p.featured ? "text-forest-100" : "text-slate-400"}`}>{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="num font-heading text-5xl font-bold">{p.price}</span>
                {p.suffix && <span className={`text-base ${p.featured ? "text-forest-100" : "text-slate-500"}`}>{p.suffix}</span>}
              </div>
              <p className={`mt-2 text-sm ${p.featured ? "text-forest-100" : "text-slate-400"}`}>{p.desc}</p>
              <ul className="mt-7 space-y-3 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-white" : "text-forest-300"}`} strokeWidth={2.5} />
                    <span className={p.featured ? "" : "text-slate-300"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className={`mt-8 w-full inline-flex justify-center items-center px-6 py-3 rounded-full font-semibold text-sm transition-all ${
                  p.featured
                    ? "bg-white text-forest hover:bg-forest-50 hover:scale-[1.02]"
                    : "bg-forest hover:bg-forest-dark text-white hover:scale-[1.02]"
                }`}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
              >
                Get started <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24 lg:py-32 bg-[#0B1220] border-t border-white/[0.06]">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6 text-white">
          Stop guessing about<br />your <span className="text-forest-300">money.</span>
        </h2>
        <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">
          The first quarter is free. After that, the average customer saves more in taxes than they pay us in a decade.
        </p>
        <Link
          to="/register"
          className="inline-flex items-center gap-2 bg-forest hover:bg-forest-dark text-white px-8 py-4 rounded-full font-semibold shadow-lg shadow-forest/30 transition-all hover:scale-[1.02]"
          data-testid="final-cta"
        >
          Create your account <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/[0.06] bg-[#080D18]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 flex flex-col md:flex-row justify-between gap-6">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-forest flex items-center justify-center text-white">
            <Building2 className="w-3.5 h-3.5" strokeWidth={1.75} />
          </div>
          <span className="font-heading text-lg font-semibold text-white">BizCashflow <span className="text-forest-300">Pros</span></span>
          <span className="text-xs text-slate-500 ml-2">© {new Date().getFullYear()}</span>
        </div>
        <div className="text-xs text-slate-500">
          BizCashflow Pros is not a tax advisor. Always consult a licensed CPA before filing.
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className={DARK_BG} data-testid="landing-page">
      <Nav />
      <Hero />
      <ProductDemo />
      <Features />
      <HowItWorks />
      <Testimonial />
      <Pricing />
      <FinalCTA />
      <Footer />
    </div>
  );
}
